import React from "react";
import {
  Train,
  RefreshCw,
  Share2,
  Check,
} from "lucide-react";
import { LiveOpsSkeleton } from "./skeletons";

/**
 * LiveOpsPanel — featured live-journey hero card.
 *
 * Renders only when at least one PNR has `live: true` (within ±6h of
 * departure). For the per-PNR live status modal, the older
 * LiveTrainStatus component is retained — this panel is strictly the
 * top-of-page Mission Control showpiece.
 *
 * Props:
 *   data       — live status payload (see mock-pro.js > mockLive)
 *   onRefresh  — () => void
 *   onShare    — () => void
 *   isRefreshing — bool; spins the refresh icon
 *
 * The progress-bar pulse position is derived from
 *   stations.findIndex(s => s.current).
 * Falls back to data.progress_pct when no station is flagged current.
 */

const StationRow = ({ s, isLast }) => {
  const tone = s.current
    ? "text-sky-300"
    : s.passed
      ? "text-zinc-500"
      : "text-zinc-400";

  return (
    <div className="grid grid-cols-[18px_56px_1fr_72px_60px] items-center gap-3 px-2 py-2 rounded-md hover:bg-zinc-800/30 relative">
      {/* timeline dot + connector */}
      <div className="flex justify-center relative">
        {!isLast && (
          <span
            className={`absolute top-3 left-1/2 -translate-x-1/2 w-px h-[28px] ${
              s.passed && !s.current ? "bg-emerald-400/30" : "bg-zinc-800"
            }`}
          />
        )}
        {s.current ? (
          <span className="relative inline-flex w-3 h-3">
            <span className="absolute inset-0 rounded-full bg-sky-400/40 animate-ping" />
            <span className="relative w-3 h-3 rounded-full bg-sky-400 ring-2 ring-sky-400/30" />
          </span>
        ) : s.passed ? (
          <span className="w-3 h-3 rounded-full bg-emerald-400/80 flex items-center justify-center">
            <Check size={8} strokeWidth={3} className="text-zinc-950" />
          </span>
        ) : (
          <span className="w-2 h-2 rounded-full bg-zinc-700 ring-2 ring-zinc-900" />
        )}
      </div>

      <span
        className={`text-[12px] font-semibold font-mono ${
          s.current ? "text-sky-300" : "text-zinc-300"
        }`}
      >
        {s.code}
      </span>

      <span className={`text-[12.5px] truncate ${tone}`}>{s.name}</span>

      <span className={`text-[11.5px] text-right font-mono ${tone}`}>
        {s.scheduled}
        {s.actual && s.actual !== "—" && s.actual !== s.scheduled && (
          <span
            className={`ml-1 ${
              s.delay > 0 ? "text-amber-400" : "text-emerald-400"
            }`}
          >
            →{s.actual}
          </span>
        )}
      </span>

      <span
        className={`text-[10.5px] text-right font-mono ${
          s.delay > 0 ? "text-amber-400" : "text-emerald-400"
        }`}
      >
        {s.passed && s.delay === 0
          ? "ON TIME"
          : s.passed
            ? `+${s.delay}m`
            : s.delay > 0
              ? `EST +${s.delay}m`
              : "—"}
      </span>
    </div>
  );
};

const LiveOpsPanel = ({ data, onRefresh, onShare, isRefreshing = false, loading = false }) => {
  if (loading) return <LiveOpsSkeleton />;

  if (!data || !data.stations || data.stations.length === 0) return null;

  const currentIdx = data.stations.findIndex((s) => s.current);
  const totalSegments = Math.max(1, data.stations.length - 1);
  const calculatedPct =
    currentIdx >= 0
      ? ((currentIdx + 0.5) / totalSegments) * 100
      : (data.progress_pct ?? 0);
  const progressPct = Math.max(0, Math.min(100, calculatedPct));

  const onTime = data.is_on_time === true || (data.delay_mins ?? 0) === 0;
  const statusValue = onTime ? "On Time" : "Running Late";
  const statusTone = onTime ? "text-emerald-400" : "text-amber-400";
  const statusSub = onTime
    ? "All clear"
    : `+${data.delay_mins ?? 0} min`;

  return (
    <div
      className="relative rounded-xl border border-sky-500/20 bg-gradient-to-br from-sky-500/[0.06] via-zinc-900/40 to-zinc-900/40 p-5 overflow-hidden"
      data-testid="live-ops-panel"
    >
      {/* Glow blobs */}
      <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center shrink-0">
            <Train size={20} className="text-sky-300" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-[15px] font-bold text-zinc-50 whitespace-nowrap font-heading">
                {data.train_name}
              </span>
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-zinc-800 border border-zinc-700 text-zinc-400 whitespace-nowrap font-mono">
                {data.train_number}
              </span>
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10.5px] font-medium bg-sky-500/15 text-sky-300 border border-sky-500/30 whitespace-nowrap">
                <span className="relative inline-flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-sky-400/60 animate-ping" />
                  <span className="relative w-1.5 h-1.5 rounded-full bg-sky-400" />
                </span>
                LIVE TRACKING
              </span>
            </div>
            <div className="text-[12px] text-zinc-400 whitespace-nowrap font-mono">
              PNR {data.pnr_number} · {data.journey_date} ·{" "}
              {data.departure_time} → {data.arrival_time}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onRefresh}
            data-testid="live-ops-refresh"
            className="px-3 h-8 whitespace-nowrap rounded-md bg-zinc-900/80 border border-zinc-800 hover:border-zinc-700 text-zinc-300 text-[12px] flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw size={12} className={isRefreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onShare}
            data-testid="live-ops-share"
            className="px-3 h-8 whitespace-nowrap rounded-md bg-zinc-900/80 border border-zinc-800 hover:border-zinc-700 text-zinc-300 text-[12px] flex items-center gap-1.5 transition-colors"
          >
            <Share2 size={12} />
            Share
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Status", value: statusValue, sub: statusSub, tone: statusTone },
          {
            label: "Next Halt",
            value: data.next_station_name || "—",
            sub:
              data.eta_to_next_mins != null
                ? `ETA · ${data.eta_to_next_mins} min`
                : "—",
            tone: "text-zinc-50",
          },
          {
            label: "Platform",
            value: data.platform_number ? `Pf ${data.platform_number}` : "—",
            sub: data.next_station_code || "—",
            tone: "text-sky-300",
          },
          {
            label: "Progress",
            value: `${data.progress_pct ?? Math.round(progressPct)}%`,
            sub: "of journey",
            tone: "text-indigo-300",
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-zinc-950/50 border border-zinc-800/70 rounded-lg p-3"
          >
            <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1 font-mono">
              {s.label}
            </div>
            <div className={`text-[18px] font-bold font-heading ${s.tone}`}>
              {s.value}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
              {s.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Route progress strip */}
      <div className="relative mb-4">
        <div className="relative h-1.5 bg-zinc-800/80 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 via-sky-400 to-sky-400 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-500"
            style={{ left: `${progressPct}%` }}
          >
            <div className="relative">
              <span className="absolute -inset-2 rounded-full bg-sky-400/30 animate-ping" />
              <span className="relative block w-3 h-3 rounded-full bg-sky-400 ring-2 ring-sky-200/40 shadow-[0_0_12px_rgba(56,189,248,0.7)]" />
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-2">
          {data.stations.map((s) => (
            <div
              key={s.code}
              className="flex flex-col items-center"
              style={{ width: `${100 / data.stations.length}%` }}
            >
              <span
                className={`text-[10px] font-semibold font-mono ${
                  s.current
                    ? "text-sky-300"
                    : s.passed
                      ? "text-emerald-400/80"
                      : "text-zinc-500"
                }`}
              >
                {s.code}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Station timeline */}
      <div className="relative bg-zinc-950/40 rounded-lg border border-zinc-800/60 p-2 max-h-[280px] overflow-y-auto">
        <div className="grid grid-cols-[18px_56px_1fr_72px_60px] items-center gap-3 px-2 pb-1.5 border-b border-zinc-800/60 mb-1">
          {["", "Code", "Station", "Time", "Delay"].map((h, i) => (
            <span
              key={i}
              className="text-[9px] tracking-[0.18em] uppercase text-zinc-600 font-semibold font-mono"
            >
              {h}
            </span>
          ))}
        </div>
        {data.stations.map((s, i) => (
          <StationRow
            key={s.code}
            s={s}
            isLast={i === data.stations.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default LiveOpsPanel;
